import { MUNICIPALITY_CODE_VIETNAM } from 'constants';

import React, { useCallback, useEffect, useState } from 'react';

import { useFormik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { reportDetail, reportExport } from 'api/endpoints/report';
import Breadcrumb from 'components/Breadcrumb';
import Button from 'components/Button';
import LayoutReview from 'components/layouts/LayoutReview';
import {
  ENUM_SURVEY_STATUS,
  ENUM_SURVEY_TYPE,
  ENUM_TYPE_ACTION,
  ENUM_TYPE_DOWNLOAD,
  ENUM_TYPE_SURVEY,
  ENUM_TYPE_SURVEY_RESPONSE,
} from 'constants/enum';
import { MESSAGE_VIEW_SDQ, MESSAGE_VIEW_SDQ_VIETNAM } from 'constants/string/messages';
import { TEXT_CREATE_SURVEY, TEXT_DOWNLOAD, TEXT_REPORT_LIST, TEXT_RESULT_REPORT } from 'constants/string/texts';
import { toastError } from 'lib/toast';
import ResultReport from 'pages/Report/ViewSDQ/ResultReport/ResultReport';
import Form from 'pages/Survey/Form/Form';
import AppRoutes from 'routes/routes';
import { setLoading } from 'store/actions/common';
import { calculatorHeightContain, handleError, handleImageSurvey } from 'utils/helper';

const DownloadFile = require('assets/resources/DownloadFile.svg').default;
const MoreVert = require('assets/resources/MoreVert.svg').default;

const ViewSDQ = () => {
  const { id } = useParams();
  const navigator = useNavigate();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { me } = useSelector((state) => state.auth);

  const [breadcrumbs] = useState([
    { title: TEXT_CREATE_SURVEY.SURVEY_LIST, event: () => navigator(AppRoutes.report) },
    { title: TEXT_REPORT_LIST.SDQ_REPORT },
  ]);
  const [dataReport, setDataReport] = useState({});
  const [dataAnalysis, setDataAnalysis] = useState({});
  const [heightReportDetail, setHeightReportDetail] = useState(0);

  const initialValues = (() => {
    const initData = {
      title: '',
      description: '',
      status: ENUM_SURVEY_STATUS.DRAFT,
      survey_type: ENUM_SURVEY_TYPE.EXAM,
      sections: [],
    };

    return initData;
  })();

  const form = useFormik({ initialValues: initialValues });

  const handleImage = async (images) => {
    return await handleImageSurvey(images);
  };

  const handleQuestion = async (question, survey_response) => {
    const processedImages = await handleImage(question.images);
    const answer = _.find(survey_response, { question_id: question.order })?.answer || null;

    return {
      ...question,
      action: ENUM_TYPE_ACTION.UPDATE,
      images: processedImages,
      answer: _.isArray(answer) ? answer : [answer],
      media_files: _.find(survey_response, { question_id: question.order })?.media_files || null,
    };
  };

  const handleSection = async (section, survey_response) => {
    const processedQuestions = [];
    let question_number = 0;
    for (const question of section.questions) {
      const questionHandled = await handleQuestion(question, survey_response);
      processedQuestions.push({
        ...questionHandled,
        question_number: question_number + 1,
        order: question_number,
      });
      question_number++;
    }

    return processedQuestions;
  };

  const handleOrder = (data, key) => {
    return data.map((item, index) => {
      return {
        ...item,
        [key]: index,
      };
    });
  };

  const handleSurveyContent = async (survey_content, survey_response, survey_types, content_types) => {
    let sortedSections = null;
    let responses = [];

    for (const [index, survey_type] of survey_types.entries()) {
      const content = survey_content.find((item) => item.survey_type === survey_type);
      // A part can be built from alternative survey types (part C is MCHAT, DIPOLE or KINDL),
      // so skip the ones this report doesn't have instead of dropping the whole part
      if (!content || _.isEmpty(content.sections)) {
        continue;
      }

      sortedSections = [...(sortedSections || []), ..._.cloneDeep(_.sortBy(content.sections, 'order'))];

      const response = _.find(survey_response, { type: content_types[index] })?.responses || [];
      responses = [...responses, ...response];
    }

    if (_.isEmpty(sortedSections)) {
      return [];
    }

    // A part can span several sections (KINDL has 8) and answers are submitted flattened
    // across all of them, so questions must be numbered globally for the answer lookup
    // in handleQuestion to line up with the responses
    let question_order = 0;
    for (const section of sortedSections) {
      section.questions = _.sortBy(section.questions || [], 'order').map((question) => ({
        ...question,
        order: question_order++,
      }));
    }
    responses = handleOrder(responses, 'question_id');

    const dataSection = [];
    for (const section of sortedSections) {
      // Skip sections that carry no questions so the part never renders as a bare
      // header with nothing under it. If every section is empty the part is dropped.
      if (_.isEmpty(section.questions)) {
        continue;
      }

      const sectionHandled = await handleSection(section, responses);
      dataSection.push({
        ...section,
        action: ENUM_TYPE_ACTION.UPDATE,
        questions: sectionHandled,
      });
    }

    return dataSection;
  };

  /**
   * Processing the question map with the answer
   * Including 6 parts: A = SDQ, B = YABUNAKA, C = KINDL (older reports: MCHAT / DIPOLE),
   * D = 日本版Parenting Scale, E = 好奇心, F = 安全性評価
   * Each part has the corresponding questions and answers, and may span several sections.
   * Parts C-F are optional, so a report that predates them simply has no sections for them.
   */
  const handleSurvey = async (survey_content, survey_response) => {
    const _MESSAGE_VIEW_SDQ = MUNICIPALITY_CODE_VIETNAM.includes(me?.municipality_code) ? MESSAGE_VIEW_SDQ_VIETNAM : MESSAGE_VIEW_SDQ;

    // A part can be built from alternative survey types (part C is MCHAT, DIPOLE or
    // KINDL); the response type at the same position is the matching snapshot key.
    const PARTS = [
      { title: _MESSAGE_VIEW_SDQ.TITLE_SDQ, types: [ENUM_TYPE_SURVEY.SDQ], responseTypes: [ENUM_TYPE_SURVEY_RESPONSE.SDQ] },
      { title: _MESSAGE_VIEW_SDQ.TITLE_YABUNAKA, types: [ENUM_TYPE_SURVEY.YABUNAKA], responseTypes: [ENUM_TYPE_SURVEY_RESPONSE.YABUNAKA] },
      {
        title: _MESSAGE_VIEW_SDQ.TITLE_MCHAT_DIPOLE,
        types: [ENUM_TYPE_SURVEY.MCHAT, ENUM_TYPE_SURVEY.DIPOLE, ENUM_TYPE_SURVEY.KINDL],
        responseTypes: [ENUM_TYPE_SURVEY_RESPONSE.MCHAT, ENUM_TYPE_SURVEY_RESPONSE.DIPOLE, ENUM_TYPE_SURVEY_RESPONSE.KINDL],
      },
      {
        title: _MESSAGE_VIEW_SDQ.TITLE_PARENTING_SCALE,
        types: [ENUM_TYPE_SURVEY.PARENTING_SCALE],
        responseTypes: [ENUM_TYPE_SURVEY_RESPONSE.PARENTING_SCALE],
      },
      {
        title: _MESSAGE_VIEW_SDQ.TITLE_CURIOSITY,
        types: [ENUM_TYPE_SURVEY.CURIOSITY],
        responseTypes: [ENUM_TYPE_SURVEY_RESPONSE.CURIOSITY],
      },
      {
        title: _MESSAGE_VIEW_SDQ.TITLE_SAFETY_ASSESSMENT,
        types: [ENUM_TYPE_SURVEY.SAFETY_ASSESSMENT],
        responseTypes: [ENUM_TYPE_SURVEY_RESPONSE.SAFETY_ASSESSMENT],
      },
    ];

    const sections = [];

    for (const part of PARTS) {
      const partSections = await handleSurveyContent(survey_content, survey_response, part.types, part.responseTypes);
      /**
       * Append every section of a part. The first one is labelled with the part name;
       * any further sections (KINDL is split into 8) keep their own title so nothing is hidden.
       */
      partSections.forEach((section, index) => {
        sections.push({ ...section, title: index === 0 ? part.title : section.title, order: sections.length });
      });
    }

    form.setValues({ sections: sections });
  };

  /**
   * Get detail report
   */
  const getDetail = async () => {
    dispatch(setLoading({ isOpen: true }));
    try {
      const result = await reportDetail(id);
      if (result.data) {
        setDataReport(result.data);
        const survey_content = result.data.survey_content;
        const survey_response = result.data.report_content;
        await handleSurvey(survey_content, survey_response);
        if (result.data.analysis_content) {
          setDataAnalysis({
            analysis_content: result.data.analysis_content,
            integrated_analysis_content: result.data.integrated_analysis_content,
          });
        }
      } else {
        navigate(AppRoutes.report);
      }
    } catch (err) {
      handleError(err);
    }
    dispatch(setLoading({ isOpen: false }));
  };

  const getReportDetail = useCallback(async () => {
    if (me) {
      await getDetail();
    }
  }, [me, id]);

  const handleDownload = async (type) => {
    dispatch(setLoading({ isOpen: true }));
    await reportExport({ report_ids: [id] }, type, me.lang)
      .then((res) => {
        dispatch(setLoading({ isOpen: false }));
      })
      .catch((error) => {
        toastError(error.message);
        dispatch(setLoading({ isOpen: false }));
      });
  };

  useEffect(() => {
    getReportDetail();
  }, [getReportDetail]);

  useEffect(() => {
    // Height for scroll inner
    const height = calculatorHeightContain(
      [{ id: 'report-detail-title' }, { id: 'breadcrumb-download' }],
      [{ id: 'main' }, { id: 'layout-report-detail' }, { id: 'report-detail-container' }]
    );
    setHeightReportDetail(height);
  }, []);

  useEffect(() => {
    document.body.classList.add('overflow-hidden-custom');
    document.body.classList.add('h-screen-custom');

    return () => {
      document.body.classList.remove('overflow-hidden-custom');
      document.body.classList.remove('h-screen-custom');
    };
  }, []);

  return (
    <>
      <section id="create-survey" className="flex-middle-start flex-wrap relative">
        <div className="flex justify-center items-start space-x-6" id="breadcrumb-download">
          <div className="w-report-detail">
            <div className="flex items-center gap-2">
              <div className="max-w-96 flex items-center gap-2">
                <Breadcrumb breadcrumbs={breadcrumbs} />
              </div>
            </div>
          </div>
          <div className="w-160">
            <div className="flex items-center justify-end gap-8 relative z-2">
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => handleDownload(ENUM_TYPE_DOWNLOAD.CSV)}
                  type="button"
                  color="white"
                  className="text-black/85 text-sm w-55.5 h-8"
                  text={TEXT_DOWNLOAD.CSV_DOWNLOAD}
                  icon={<img className="mr-2.5" src={DownloadFile} alt="download file icon" width={14} height={14} />}
                />
                <Button
                  onClick={() => handleDownload(ENUM_TYPE_DOWNLOAD.EXCEL)}
                  type="button"
                  color="white"
                  className="text-black/85 text-sm w-55.75 h-8"
                  text={TEXT_DOWNLOAD.EXCEL_DOWNLOAD}
                  icon={<img className="mr-2.5" src={DownloadFile} alt="download file icon" width={14} height={14} />}
                />

                <div className="relative flex items-center">
                  <button type="button" className="w-6 h-8">
                    <img src={MoreVert} alt="more vert icon" width={24} height={24} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="report-detail-container" className="flex justify-center items-start space-x-6 pt-6.5">
          <div className="w-report-detail">
            <div className="pb-4" id="report-detail-title">
              <h1 className="text-2xl font-bold text-black-102 text-center">{TEXT_RESULT_REPORT.ANSWER_RESULT}</h1>
            </div>
            <LayoutReview className="max-w-full !px-0" id="layout-report-detail">
              <form id="report-detail-form" className="create-survey overflow-y-auto px-8" style={{ height: heightReportDetail }}>
                <div className="flex items-center space-x-6 text-black/85 text-sm mb-8">
                  {form.values.title && (
                    <span>
                      {TEXT_CREATE_SURVEY.TEMPLATE_TITLE}：{form.values.title}
                    </span>
                  )}
                  {dataReport.child_id && (
                    <span>
                      {TEXT_REPORT_LIST.USER_ID}：{dataReport.child_id}
                    </span>
                  )}
                  {dataReport.created_at && (
                    <span>
                      {TEXT_REPORT_LIST.ANSWER_DAY}：{moment(dataReport.created_at).format('YYYY/MM/DD')}
                    </span>
                  )}
                </div>
                <Form
                  isShowVietName={MUNICIPALITY_CODE_VIETNAM.includes(me?.municipality_code)}
                  form={form}
                  viewOnly={true}
                  showControlMenu={false}
                  showTitle={false}
                />
              </form>
            </LayoutReview>
          </div>

          {dataAnalysis && (dataAnalysis?.analysis_content?.child_id || dataAnalysis?.integrated_analysis_content?.child_id) && (
            <div className="w-160">
              <div className="pb-4">
                <h1 className="text-2xl font-bold text-black-102 text-center">{TEXT_RESULT_REPORT.ANALYSIS_REPORT}</h1>
              </div>
              <LayoutReview className="max-w-full !px-0">
                <div id="report-detail-analysis" className="create-survey overflow-y-auto px-8" style={{ maxHeight: heightReportDetail }}>
                  <ResultReport dataReport={dataReport} dataAnalysis={dataAnalysis} />
                </div>
              </LayoutReview>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default React.memo(ViewSDQ);
